import LegalPage from "@/pages/legal/LegalPage";

export default function Privacy() {
  return (
    <LegalPage title="Privacy Policy" updated="February 5, 2026">
      <p>
        This Privacy Policy explains how Immersive Character AI ("<strong>we</strong>",
        "<strong>us</strong>") collects, uses, and safeguards your information when you use
        our website at <a href="https://imrsvprtl.com">imrsvprtl.com</a> and related services
        (the "<strong>Service</strong>").
      </p>

      <h2>1. Information We Collect</h2>

      <h3>Information you give us</h3>
      <ul>
        <li><strong>Account data</strong> — email address, username, password (hashed), date of birth</li>
        <li><strong>Profile data</strong> — display name, avatar, bio (if you provide one)</li>
        <li><strong>Content</strong> — characters, personas, and chat messages you create</li>
        <li><strong>Age verification</strong> — a selfie photo (if you opt in to NSFW content). We use an AI model to confirm the subject appears to be an adult; the photo is not retained after verification completes</li>
        <li><strong>Payment info</strong> — billing details for Immersive+ subscriptions. These are processed and stored directly by Stripe. We only receive a subscription status and non-sensitive metadata</li>
        <li><strong>Communications</strong> — feedback and support messages you send us</li>
      </ul>

      <h3>Information collected automatically</h3>
      <ul>
        <li><strong>Usage data</strong> — pages visited, features used, timestamps</li>
        <li><strong>Device data</strong> — browser type, operating system, IP address (for security and abuse prevention)</li>
        <li><strong>Cookies</strong> — a session cookie for keeping you signed in. We do not use third-party advertising cookies</li>
      </ul>

      <h2>2. How We Use Your Information</h2>
      <ul>
        <li>To operate the Service (authenticate you, deliver AI-generated responses, save your characters and chats)</li>
        <li>To process payments and manage subscriptions</li>
        <li>To send transactional email (password reset, subscription receipts, admin replies)</li>
        <li>To detect and prevent fraud, abuse, and Terms of Service violations</li>
        <li>To improve the Service and develop new features</li>
        <li>To comply with legal obligations</li>
      </ul>
      <p>
        <strong>We do not sell your personal information.</strong> We do not use your chat
        content to train third-party AI models beyond the transient processing required to
        generate a response.
      </p>

      <h2>3. AI Processing of Your Content</h2>
      <p>
        Chat messages, character descriptions, and personas you create are transmitted to
        Anthropic's Claude API to generate responses. Anthropic processes this data
        transiently and does not retain it for training under its enterprise API terms.
        Similarly, avatar prompts are sent to Google's Nano Banana model for image
        generation and are not retained.
      </p>

      <h2>4. How We Share Information</h2>
      <p>We share information only with:</p>
      <ul>
        <li><strong>Service providers</strong> — Anthropic, Google, Stripe, Resend, and our hosting/database providers, each bound by contract and data-protection standards</li>
        <li><strong>Legal &amp; safety</strong> — law enforcement or other authorities when required by law, subpoena, or to protect the safety of users. This includes reporting Child Sexual Abuse Material (CSAM) to NCMEC</li>
        <li><strong>Business transfers</strong> — if we are acquired or merge, your information may be transferred to the acquiring entity subject to this Policy</li>
      </ul>

      <h2>5. Data Retention</h2>
      <p>
        We retain your account and content for as long as your account is active. When you
        delete your account (Settings → Delete Account), we permanently delete your
        characters, chats, personas, feedback, and reset tokens within 30 days. Backups may
        retain data for up to 90 days after deletion.
      </p>

      <h2>6. Security</h2>
      <p>
        We protect your information using industry-standard practices: passwords hashed
        with bcrypt, TLS for all data in transit, restricted database access, and regular
        security reviews. No system is 100% secure, however, and we cannot guarantee
        absolute security.
      </p>

      <h2>7. Your Rights</h2>
      <p>Depending on your jurisdiction, you may have the right to:</p>
      <ul>
        <li>Access the personal data we hold about you</li>
        <li>Correct inaccurate data</li>
        <li>Delete your data (via the "Delete Account" flow, or by emailing us)</li>
        <li>Export your data in a machine-readable format (subscribers can export chats as PDF/HTML/MD from within the app; other data on request)</li>
        <li>Object to or restrict certain processing</li>
        <li>Withdraw consent for optional processing at any time</li>
      </ul>
      <p>
        To exercise these rights, email <a href="mailto:privacy@imrsvprtl.com">privacy@imrsvprtl.com</a>.
        We will respond within 30 days.
      </p>

      <h3>California residents (CCPA)</h3>
      <p>
        California residents have additional rights to know what personal information we
        collect, delete it, and opt out of the "sale" of personal information (we do not sell
        it). Requests can be made through the email above.
      </p>

      <h3>European residents (GDPR)</h3>
      <p>
        We process your data under legitimate interest, contract performance, and consent
        as applicable. You have the right to lodge a complaint with your local supervisory
        authority.
      </p>

      <h2>8. Children</h2>
      <p>
        The Service is <strong>not intended for anyone under 18</strong>. We do not knowingly
        collect information from minors. If we discover that a minor has created an account,
        we will delete it immediately. If you believe a minor is using the Service, contact
        us at <a href="mailto:support@imrsvprtl.com">support@imrsvprtl.com</a>.
      </p>

      <h2>9. International Transfers</h2>
      <p>
        We operate from Texas, USA. If you access the Service from outside the United States,
        your information will be transferred to and processed in the United States and other
        countries where our service providers operate. By using the Service you consent to
        this transfer.
      </p>

      <h2>10. Changes to This Policy</h2>
      <p>
        We may update this Privacy Policy from time to time. Material changes will be
        announced by email or in-app notice at least 14 days before they take effect.
      </p>

      <h2>11. Contact</h2>
      <p>
        General questions: <a href="mailto:support@imrsvprtl.com">support@imrsvprtl.com</a><br />
        Privacy requests: <a href="mailto:privacy@imrsvprtl.com">privacy@imrsvprtl.com</a><br />
        Mailing address: available on request
      </p>
    </LegalPage>
  );
}
